import { useRef } from 'react';

import useClientState from '@/state/useClientState';
import { Overgang } from '@/types/deeltrajecten';

import { MwdDeeltraject } from '../../types/Domain';
import { WerklijnLegendLeft, WerklijnLegendRight } from '../../types/legends';
import { MousePointer } from '../types/ActionStore';
import { TooltipData, TooltipKey } from '../types/TooltipStore';

import { deeltrajectToTooltipDetails, TOOLTIP_MOUSE_MOVE_DELAY } from './tooltip';

export default function useTooltipActions() {
  const setShowTooltip = useClientState((state) => state.setShowTooltip);
  const setTooltipTimeoutId = useClientState((state) => state.setTooltipTimeoutId);

  const mouseMoveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltipWithTimeout = async (tooltipKey: TooltipKey, mousePointer: MousePointer, data: TooltipData) => {
    if (mouseMoveTimeoutRef.current) clearTimeout(mouseMoveTimeoutRef.current);
    mouseMoveTimeoutRef.current = setTimeout(async () => {
      await setShowTooltip(tooltipKey, mousePointer, data);
    }, TOOLTIP_MOUSE_MOVE_DELAY);
  };

  const hideTooltipWithTimeout = async (timeout = TOOLTIP_MOUSE_MOVE_DELAY) => {
    if (mouseMoveTimeoutRef.current) clearTimeout(mouseMoveTimeoutRef.current);
    await setTooltipTimeoutId(timeout);
  };

  const setShowDeeltrajectTooltip = async (mousePointer: MousePointer, deeltraject: MwdDeeltraject) => {
    const tooltipDetails = deeltrajectToTooltipDetails(deeltraject);
    await showTooltipWithTimeout('deeltraject', mousePointer, tooltipDetails);
  };

  const setShowOvergangTooltip = async (mousePointer: MousePointer, overgang) => {
    // For overgangs before the first inzet or after the last inzet, we don't show a tooltip.
    if (!overgang.isOvergangVoorEersteInzet && !overgang.isOvergangNaLaatsteInzet) {
      await showTooltipWithTimeout('overgang', mousePointer, overgang as unknown as Overgang);
    }
  };

  const setShowLeftLegendTooltip = async (mousePointer: MousePointer, leftLegend: WerklijnLegendLeft) => {
    await showTooltipWithTimeout('leftLegend', mousePointer, leftLegend);
  };

  const setShowLeftLegendPerronLengteConflictTooltip = async (
    mousePointer: MousePointer,
    leftLegend: WerklijnLegendLeft,
  ) => {
    await showTooltipWithTimeout('leftLegendPerronLengteConflicten', mousePointer, leftLegend);
  };

  const setShowRightLegendTooltip = async (mousePointer: MousePointer, rightLegend: WerklijnLegendRight) => {
    await showTooltipWithTimeout('rightLegend', mousePointer, rightLegend);
  };

  const setShowDetailPanelOnTimesTooltip = async (mousePointer: MousePointer, detailPanel) => {
    await showTooltipWithTimeout('detailPanelOnTimes', mousePointer, detailPanel);
  };

  const setShowDetailPanelOnSpoorTooltip = async (mousePointer: MousePointer, detailPanel) => {
    await showTooltipWithTimeout('detailPanelOnSpoor', mousePointer, detailPanel);
  };
  const setShowDetailPanelOnPrognoseTooltip = async (mousePointer: MousePointer, detailPanel) => {
    await showTooltipWithTimeout('detailPanelOnPrognose', mousePointer, detailPanel);
  };

  return {
    setShowTooltip,
    hideTooltipWithTimeout,
    setTooltipTimeoutId,
    setShowDeeltrajectTooltip,
    setShowOvergangTooltip,
    setShowLeftLegendTooltip,
    setShowLeftLegendPerronLengteConflictTooltip,
    setShowRightLegendTooltip,
    setShowDetailPanelOnTimesTooltip,
    setShowDetailPanelOnSpoorTooltip,
    setShowDetailPanelOnPrognoseTooltip,
  };
}
